import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from './JobDetailNew.module.css'; // Reusing your existing styles!
import api from '../../api/api.js';
import showToast from "../../components/ShowToast";
import Loader2 from '../../components/loader2';
//import PostcodeAutocomplete from "../../components/PostcodeAutocomplete/PostcodeAutocomplete"; 
import LocationInputGroup from '../../components/Form/LocationInputGroup';
import { FileText, Camera, DollarSign, Megaphone, UserPlus, Clock, ArrowLeft, ListChecks, Check } from 'lucide-react';
import JobFileUploader from '../../components/Form/JobFileUploader';
import ProfessionSelector from '../../components/Form/ProfessionSelector';
import ModernNavbarPureCSS from '../../components/Navbar/ModernNavBarPureCSS';
import { jwtDecode } from 'jwt-decode';

// --- INTERNAL COMPONENT: Wizard Progress Bar ---
const WizardProgressBar = ({ currentStep }) => {
    const steps = [
        { id: 1, label: 'Service' },
        { id: 2, label: 'Confirm' },
        { id: 3, label: 'Details' },
        { id: 4, label: 'Logistics' },
        { id: 5, label: 'Photos' }
    ];

    // Calculate progress width (0% at step 1, 100% at step 5)
    const progressPercentage = Math.min(100, Math.max(0, ((currentStep - 1) / (steps.length - 1)) * 100));

    return (
        <div style={{ maxWidth: '800px', margin: '0 auto 2rem auto', padding: '0 1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', position: 'relative', marginBottom: '10px' }}>
                {/* Background Line */}
                <div style={{ 
                    position: 'absolute', top: '50%', left: '0', right: '0', height: '2px', 
                    backgroundColor: '#e5e7eb', zIndex: 0, transform: 'translateY(-50%)' 
                }}></div>
                
                {/* Active Progress Line */}
                <div style={{ 
                    position: 'absolute', top: '50%', left: '0', height: '2px', 
                    width: `${progressPercentage}%`, backgroundColor: '#0066ff', 
                    zIndex: 0, transform: 'translateY(-50%)', transition: 'width 0.3s ease' 
                }}></div>

                {steps.map((step) => {
                    const isCompleted = step.id < currentStep;
                    const isActive = step.id === currentStep;
                    
                    return (
                        <div key={step.id} style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div style={{ 
                                width: '30px', height: '30px', borderRadius: '50%', 
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                backgroundColor: isCompleted || isActive ? '#0066ff' : '#fff',
                                border: isCompleted || isActive ? 'none' : '2px solid #e5e7eb',
                                color: isCompleted || isActive ? '#fff' : '#6b7280',
                                fontWeight: '600', fontSize: '0.8rem',
                                transition: 'all 0.3s ease'
                            }}>
                                {isCompleted ? <Check size={16} /> : step.id}
                            </div>
                            <span style={{ 
                                marginTop: '8px', fontSize: '0.75rem', fontWeight: isActive ? '600' : '500', 
                                color: isActive ? '#0066ff' : '#6b7280',
                                display: 'none' // Hide labels on very small screens if needed, or manage via CSS
                            }} className={styles.hideOnMobile}>
                                {step.label}
                            </span>
                        </div>
                    );
                })}
            </div>
            {/* Mobile Labels (Optional, if needed) */}
            <div style={{ textAlign: 'center', fontSize: '0.9rem', fontWeight: '600', color: '#374151', marginTop: '0.5rem' }}>
                {steps.find(s => s.id === currentStep)?.label}
            </div>
        </div>
    );
};

// --- Main Wizard Component ---
const JobCreationWizard = () => {

    const navigate = useNavigate();
    const location = useLocation();

    const [step, setStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [username, setUsername] = useState('');
    
    // Initial State
    const [jobData, setJobData] = useState({
        searchTerm: '',
        aiSuggestions: [],
        professions: [], 
        title: '',
        description: '',
        postcode: '',
        suburb: '',
        state: '',
        timeline: '',
        budgetRange: '',
        customBudget: '',
        images: [],
        projectId: null,
        publishMode: 'broadcast',
        taskId: null
    });

    const [projectId, setProjectId] = useState(null);

    // --- USER AUTH CHECK ---
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const decoded = jwtDecode(token);
                setUsername(decoded.UserName || decoded.unique_name || 'User');
            } catch (e) {
                console.error("Token decode error", e);
            }
        }
    }, []);

    // --- INITIALIZATION EFFECT ---
    useEffect(() => {
        // 1. Check if we have incoming data
        if (location.state) {
            console.log("DEBUG: Wizard received state:", location.state);

            const { 
                projectId, taskId, taskName, taskDescription, 
                professionId, professionName, 
                postcode, suburb, projectState, mode 
            } = location.state;

            // 2. Prepare the updates
            // We use a functional update to ensure we don't overwrite initial state accidentally
            setJobData(prev => {
                const updatedData = {
                    ...prev,
                    projectId: projectId ? parseInt(projectId) : null,
                    taskId: taskId ? parseInt(taskId) : null,
                    
                    // PRE-FILL TITLE & DESCRIPTION
                    title: taskName || prev.title,
                    description: taskDescription || prev.description,
                    
                    // PRE-FILL LOCATION
                    postcode: postcode || prev.postcode,
                    suburb: suburb || prev.suburb,
                    state: projectState || prev.state,
                    
                    publishMode: mode || 'broadcast',
                };

                // PRE-FILL PROFESSION (If valid ID exists)
                if (professionId && professionName) {
                    updatedData.professions = [{ 
                        professionId: parseInt(professionId), 
                        name: professionName 
                    }];
                }

                // PRE-FILL SEARCH TERM (If no profession, but we have a task name)
                if (!professionId && taskName) {
                    updatedData.searchTerm = taskName;
                }

                return updatedData;
            });

            // 3. DETERMINE STARTING STEP
            if (professionId && professionName) {
                console.log("DEBUG: Profession found, skipping to Step 3");
                setStep(3); // Go to Details
            } else if (taskName) {
                console.log("DEBUG: Task name found (no profession), staying on Step 1 with search term");
                setStep(1); // Stay on Search, but input will be filled
            }
        }
    }, [location.state]);


    // Persist postcode between steps as a safety against any UI resets
    const postcodeRef = useRef(jobData.postcode || '');
    const suburbRef = useRef(jobData.suburb || '');

    useEffect(() => {
        if (typeof jobData.postcode === 'string' && jobData.postcode.trim()) {
            postcodeRef.current = jobData.postcode;
        }
    }, [jobData.postcode]);

    useEffect(() => {
        if (typeof jobData.suburb === 'string' && jobData.suburb.trim()) {
            suburbRef.current = jobData.suburb;
        }
    }, [jobData.suburb]);

    const updateJobData = (field, value) => {
        setJobData(prev => ({ ...prev, [field]: value }));
    };

    // This handler decides where to go from Step 1.
    const handleStep1Next = (isAdvancedSearch = false) => {
        if (isAdvancedSearch) {
            // If the advanced search was used, go to the confirmation screen.
            setStep(2);
        } else {
            // If the user selected tags manually, skip the confirmation and go straight to details.
            setStep(3);
        }
    };

    const handlePrevStep = (currentStep) => {
        if (currentStep === 3 && jobData.aiSuggestions.length > 0) {
            // If we're going back from Step 3 AND we got here via Advanced Search,
            // go back to the confirmation screen (Step 2).
            setStep(2);
        } else {
            // Otherwise, always go back to Step 1.
            setStep(1);
        }
    };
    
    const handleCancel = () => {

        const myType = localStorage.getItem('userType');
        console.log("UserType = ", myType);

        if (window.confirm("Are you sure you want to cancel? Any unsaved progress will be lost.")) {

            if (myType == "Home") {
                navigate('/newhome'); 
            } else {
                navigate('/businessdash'); 
            }
            
        }
    };

    const handleSubmit = async () => {
        // Validation checks...
        if (jobData.professions.length === 0) { showToast('error', 'Please select at least one profession.'); setStep(2); return; }
        if (!jobData.title.trim() || jobData.title.trim().length < 10) { showToast('error', 'Please enter a descriptive job title (min 10 characters).'); setStep(3); return; }
        
        const effectivePostcode = (jobData.postcode ?? '').toString().trim() || (postcodeRef.current ?? '').toString().trim();
        const effectiveSuburb = (jobData.suburb ?? '').toString().trim() || (suburbRef.current ?? '').toString().trim();

        if (!effectivePostcode) { showToast('error', 'Please enter a postcode.'); setStep(3); return; }

        console.log("--------------------------------");
        console.log("DEBUG: Submitting Job");
        console.log("Task ID:", jobData.taskId);     // Should now show 140
        console.log("Project ID:", jobData.projectId); // Should show ID
        console.log("State:", jobData.state);          // Should show NSW/VIC etc
        console.log("--------------------------------");


        setIsLoading(true);
        try {

            const myType = localStorage.getItem('userType');
            console.log("UserType = ", myType);


            const homeUserId = localStorage.getItem('loggedInHomeUserId');
            const businessUserId = localStorage.getItem('loggedInBusinessUserId');

            let myUserId = 0;
            let myUserType = "HomeUser";

            if (myType == "Home") {
                myUserId = homeUserId;
            } else {
                myUserId = businessUserId;
                myUserType = "BusinessUser";
            }

            if (!myUserId) throw new Error("Could not identify user. Please log in again.");

            // 1. Prepare Payload
            const payload = {
                userId: parseInt(myUserId, 10),
                name: jobData.title,
                description: jobData.description,
                profession: jobData.professions.map(p => ({ ProfessionId: p.professionId, name: p.name })),
                
                // Location Details
                postcode: effectivePostcode,
                mystate: jobData.state, // Ensure this is set from jobData
                suburb: effectiveSuburb,

                budget: jobData.budgetRange,
                budgetAmount: jobData.customBudget ? parseFloat(jobData.customBudget) : null,
                deadline: jobData.timeline,
                siteVisit: "By appointment",
                isPublish: jobData.publishMode === 'broadcast',
                isNew: true,
                userType: myUserType,

                // --- FIX: Explicitly send IDs ---
                projectId: jobData.projectId || null, 
                taskId: jobData.taskId || null 
            };

            console.log('[JobCreationWizard] Submitting Payload:', payload); // Debug Log

            const response = await api.post('/api/Jobs/createjob', payload);
            const newJobId = response.data.job_id;

            // Image Upload Logic...
            if (newJobId && jobData.images.length > 0) {
                const formData = new FormData();
                jobData.images.forEach(img => formData.append('files', img.file));
                await api.post(`/api/Jobs/upload/${newJobId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            }

            const isPublish = jobData.publishMode;

            showToast('success', isPublish ? 'Job Posted Publicly!' : 'Job Saved.');
            
            // Navigation Logic...
            if (!isPublish) {
                // OLD: navigate(`/search?category=${category}&inviteJobId=${newJobId}`);
                
                // NEW: Go to Console, and tell it to open the modal immediately
                navigate(`/job-console/${newJobId}`, { 
                    state: { openInviteModal: true } 
                });
            } else {
                // Broadcast Mode
                navigate(`/job-console/${newJobId}`);
            }

        } catch (error) {
            console.error(error);
            showToast('error', `Failed to post job: ${error.response?.data?.message || error.message}`);
        } finally {
            setIsLoading(false);
        }
    };


    
    const renderStep = () => {
        switch (step) {
            case 1:
                return <Step1_Profession onUpdate={updateJobData} onNext={handleStep1Next} setIsLoading={setIsLoading} jobData={jobData} onCancel={handleCancel} />;
            case 2:
                return <Step2_ConfirmProfessions onUpdate={updateJobData} onNext={() => setStep(3)} onBack={() => setStep(1)} jobData={jobData} onCancel={handleCancel} />;
            case 3:
                return <Step3_JobDetails onUpdate={updateJobData} onNext={() => setStep(4)} onBack={() => handlePrevStep(3)} jobData={jobData} onCancel={handleCancel} />;
            case 4:
                return <Step4_Logistics onUpdate={updateJobData} onNext={() => setStep(5)} onBack={() => setStep(3)} jobData={jobData} onCancel={handleCancel} />;
            case 5:
                return <Step5_ImagesAndSubmit onUpdate={updateJobData} onBack={() => setStep(4)} onSubmit={handleSubmit} jobData={jobData} onCancel={handleCancel} />;
            default:
                return <Step1_Profession onUpdate={updateJobData} onNext={handleStep1Next} setIsLoading={setIsLoading} jobData={jobData} onCancel={handleCancel} />;
        }
    };

    return (
        <div style={{minHeight: '100vh', backgroundColor: '#f9fafb', display: 'flex', flexDirection: 'column', paddingTop: '60px'}}>
            <ModernNavbarPureCSS UserName={username} />
            
            {/* Gold Subtitle Bar */}
            <div style={{
                background: 'linear-gradient(to bottom, #d4af37 0%, #f9e68c 30%, #c49c25 60%, #8a6e14 100%)',
                padding: '1.5rem 2rem',
                borderTop: '1px solid #fffae0',
                borderBottom: '1px solid #49310a',
                color: 'white',
                marginBottom: '2rem'
            }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600', color: '#011746' }}>Post a New Job</h1>
                        <p style={{ margin: '0.25rem 0 0 0', color: '#011746', opacity: 0.9 }}>Connect with top-rated professionals for your project</p>
                    </div>
                    <button 
                        onClick={handleCancel}
                        style={{
                            background: 'rgba(255, 255, 255, 0.2)',
                            border: '1px solid rgba(255, 255, 255, 0.4)',
                            color: '#011746',
                            padding: '0.5rem 1rem',
                            borderRadius: '0.375rem',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            transition: 'all 0.2s'
                        }}
                    >
                        Cancel
                    </button>
                </div>
            </div>

            <div style={{ flex: 1, paddingBottom: '3rem' }}>
                <WizardProgressBar currentStep={step} />
                
                <div className={`${styles.jobDetailNew_container} ${styles.jobDetailNew_mainContent}`} style={{ marginTop: '0' }}>
                    {isLoading ? <Loader2 /> : renderStep()}
                </div>
            </div>
        </div>
    );
};




// --- CORRECTED Step 1 Component with Hybrid Logic ---
const Step1_Profession = ({ onUpdate, onNext, setIsLoading, jobData, onCancel }) => {
    
    const handleUpdateProfessions = (newProfessions) => {
        onUpdate('professions', newProfessions);
    };

    return (
        <div className={styles.jobDetailNew_card}>
            <div className={styles.jobDetailNew_cardContent}>
                <h2 className={styles.jobDetailNew_cardTitle} style={{justifyContent: 'center', fontSize: '1.5rem', marginBottom: '1rem'}}>What do you need done?</h2>
                <p className={styles.jobDetailNew_textGray600} style={{textAlign: 'center', marginBottom: '2rem'}}>Describe your job in your own words. We'll find the right professionals for you.</p>
                
                <ProfessionSelector 
                    selectedProfessions={jobData.professions || []}
                    onUpdate={handleUpdateProfessions}
                />

                <div style={{marginTop: '2rem', display: 'flex', justifyContent: 'flex-end', gap: '1rem'}}>
                    <button onClick={() => onNext(false)} disabled={!jobData.professions || jobData.professions.length === 0} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonPrimary}`} style={{padding: '10px 40px'}}>Next</button>
                </div>
            </div>
        </div>
    );
};


// --- CORRECTED Step 2 Component ---
const Step2_ConfirmProfessions = ({ onUpdate, onNext, onBack, jobData, onCancel }) => {
    
    const handleCheckboxChange = (profession, isChecked) => {
        // The profession object from aiSuggestions already has { professionId, name }
        // because we mapped it in the fallback search.
        let currentSelections = [...jobData.professions];

        if (isChecked) {
            if (!currentSelections.some(p => p.professionId === profession.professionId)) {
                currentSelections.push(profession);
            }
        } else {
            currentSelections = currentSelections.filter(p => p.professionId !== profession.professionId);
        }
        onUpdate('professions', currentSelections);
    };

    return (
         <div className={styles.jobDetailNew_card}>
             <div className={styles.jobDetailNew_cardHeader}>
                <h3 className={styles.jobDetailNew_cardTitle}>
                    <ListChecks className={`${styles.jobDetailNew_iconMd} ${styles.jobDetailNew_textGreen500}`} />
                    Confirm the professionals to notify
                </h3>
             </div>
             <div className={styles.jobDetailNew_cardContent}>
                <p className={styles.jobDetailNew_textGray600} style={{marginBottom: '1.5rem'}}>
                    Based on your request, our AI suggests notifying the following professionals. You can adjust the selection.
                </p>
                
                 <div className={styles.jobDetailNew_cardContentSpaceY4}>
                    {/* --- FIX #2: Defensive Rendering --- */}
                    {Array.isArray(jobData.aiSuggestions) && jobData.aiSuggestions.length > 0 ? (
                        jobData.aiSuggestions.map(prof => (
                            <div key={prof.id} className={styles.jobDetailNew_checkboxContainer}>
                                <input 
                                    type="checkbox" 
                                    id={`prof-${prof.id}`}
                                    checked={Array.isArray(jobData.professions) && jobData.professions.some(p => p.professionId === prof.id)}
                                    onChange={(e) => handleCheckboxChange(prof, e.target.checked)}
                                    style={{width: '20px', height: '20px', cursor: 'pointer', marginRight: '10px'}}
                                />
                                <label htmlFor={`prof-${prof.id}`} style={{fontWeight: 500, cursor: 'pointer', fontSize: '1rem'}}>
                                    {prof.name}
                                </label>
                            </div>
                        ))
                    ) : (
                        <p className={styles.jobDetailNew_textGray500}>
                            The AI couldn't find a direct match. Please go back and try rephrasing your request.
                        </p>
                    )}
                </div>


                <div style={{display: 'flex', justifyContent: 'space-between', marginTop: '2rem'}}>
                    <button onClick={onBack} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonOutline}`}>
                        <ArrowLeft size={16} style={{marginRight: '8px'}}/> Back
                    </button>
                    <button onClick={onNext} disabled={jobData.professions.length === 0} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonPrimary}`}>
                        Next
                    </button>
                </div>
             </div>
        </div>
    );
};


// --- NEW Step 3: Job Details ---
const Step3_JobDetails = ({ jobData, onUpdate, onNext, onBack, onCancel }) => {

    /*
    const handlePostcodeSelect = (location) => {
        onUpdate('postcode', location.fullAddress);
        onUpdate('state', location.state);
    };
    */

    // Standard handler
    const handleLocationSelect = (loc) => {
        onUpdate('locationDisplay', loc.fullAddress);
        onUpdate('postcode', loc.postcode);
        onUpdate('suburb', loc.suburb);
        onUpdate('state', loc.state);
    };

    const handleNext = () => {
        if (!jobData.title || jobData.title.trim().length < 5) {
             showToast('error', 'Please enter a valid job title (at least 5 characters).');
             return;
        }
        if (!jobData.description || jobData.description.trim().length < 10) {
             showToast('error', 'Please enter a job description (at least 10 characters).');
             return;
        }
        if (!jobData.postcode) {
             showToast('error', 'Please select a location from the list.');
             return;
        }
        onNext();
    };

    return (
        <div className={styles.jobDetailNew_card}>
            <div className={styles.jobDetailNew_cardHeader}>
                <h3 className={styles.jobDetailNew_cardTitle}><FileText className={`${styles.jobDetailNew_iconMd} ${styles.jobDetailNew_textBlue600}`} />Describe your job</h3>
            </div>
            <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY6}`}>
                <div>
                    <label htmlFor="title" className={styles.jobDetailNew_label}>Job Title</label>
                    <input id="title" type="text" placeholder="e.g., Urgent repair for kitchen sink pipe" value={jobData.title || jobData.searchTerm} onChange={e => onUpdate('title', e.target.value)} className={styles.jobDetailNew_input} />
                    <p style={{fontSize: '0.8rem', color: '#6b7280', marginTop: '4px'}}>A clear title gets better responses.</p>
                </div>
                <div>
                    <label htmlFor="description" className={styles.jobDetailNew_label}>Description</label>
                    <textarea id="description" placeholder="The more detail, the better the quotes. Mention materials, dimensions, and desired outcome." value={jobData.description} onChange={e => onUpdate('description', e.target.value)} className={styles.jobDetailNew_textarea} rows={4}></textarea>
                </div>

                <LocationInputGroup 
                    label="Where is the job located?"
                    value={jobData.locationDisplay}
                    onLocationSelect={handleLocationSelect}
                    // Optional: Pass feedback props
                    suburb={jobData.suburb}
                    state={jobData.state}
                    // Optional: Pass error if you have validation state
                    error={!jobData.postcode && jobData.locationDisplay ? "Please select a valid address from the list" : null}
                />
                
                <div style={{display: 'flex', justifyContent: 'space-between', marginTop: '2rem'}}>
                    <button onClick={onBack} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonOutline}`}><ArrowLeft size={16} style={{marginRight: '8px'}}/> Back</button>
                    <button onClick={handleNext} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonPrimary}`}>Next</button>
                </div>
            </div>
        </div>
    );
};

// --- NEW Step 4: Logistics ---
const Step4_Logistics = ({ jobData, onUpdate, onNext, onBack, onCancel }) => {
    const budgetRanges = ["$500 - $1000", "$1000 - $2500", "$2500 - $5000", "$5000 - $10000", "$10000+"];
    const timelineOptions = ["Urgently", "Within 1 week", "Within 2 weeks", "2 weeks or longer"];

    const handleNext = () => {
        if (!jobData.budgetRange && !jobData.customBudget) {
             showToast('error', 'Please select a budget range or enter an amount.');
             return;
        }
        if (!jobData.timeline) {
             showToast('error', 'Please select when you need the job done.');
             return;
        }
        onNext();
    };

    return (
        <div className={styles.jobDetailNew_card}>
            <div className={styles.jobDetailNew_cardHeader}>
                <h3 className={styles.jobDetailNew_cardTitle}><DollarSign className={`${styles.jobDetailNew_iconMd} ${styles.jobDetailNew_textGreen500}`} />Budget & Timeline</h3>
            </div>
            <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY6}`}>
                <div>
                    <label className={styles.jobDetailNew_label}>What is your estimated budget?</label>
                     <div className={`${styles.jobDetailNew_grid} ${styles.jobDetailNew_md_gridCols2}`} style={{gap: '1rem'}}>
                        <select value={jobData.budgetRange} onChange={e => onUpdate('budgetRange', e.target.value)} className={styles.jobDetailNew_selectTrigger}>
                            <option value="">Select a range</option>
                            {budgetRanges.map(range => <option key={range} value={range}>{range}</option>)}
                        </select>
                        <div className={styles.jobDetailNew_inputWithIconContainer}>
                            <DollarSign className={styles.jobDetailNew_inputIcon} />
                            <input type="number" placeholder="Or enter exact amount" value={jobData.customBudget} onChange={e => onUpdate('customBudget', e.target.value)} className={`${styles.jobDetailNew_input} ${styles.jobDetailNew_inputPl10}`} />
                        </div>
                    </div>
                </div>
                 <div>
                    <label className={styles.jobDetailNew_label}>How soon do you need it done?</label>
                    <div className={styles.jobDetailNew_timelineButtonGrid}>
                        {timelineOptions.map(option => (
                             <button key={option} type="button" className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_timelineButton} ${jobData.timeline === option ? styles.jobDetailNew_bgBlue500 : styles.jobDetailNew_buttonOutline}`} onClick={() => onUpdate('timeline', option)}>
                                <span className={styles.jobDetailNew_timelineButtonText}>{option}</span>
                             </button>
                        ))}
                    </div>
                </div>
                <div style={{display: 'flex', justifyContent: 'space-between', marginTop: '2rem'}}>
                    <button onClick={onBack} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonOutline}`}><ArrowLeft size={16} style={{marginRight: '8px'}}/> Back</button>
                    <button onClick={handleNext} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonPrimary}`}>Next</button>
                </div>
            </div>
        </div>
    );
};



const getFileType = (fileName) => {
    if (!fileName) return 'unknown';
    const lower = fileName.toLowerCase();
    
    if (/\.(jpg|jpeg|png|gif|webp)$/.test(lower)) return 'image';
    if (/\.(mp4|webm|ogg|mov)$/.test(lower)) return 'video';
    if (/\.(pdf|doc|docx|xls|xlsx|csv|txt)$/.test(lower)) return 'document';
    
    return 'unknown';
};

const Step5_ImagesAndSubmit = ({ jobData, onUpdate, onBack, onSubmit, onCancel }) => {
    
    // 1. Determine the Mode (Broadcast vs Invite)
    const isBroadcast = jobData.publishMode === 'broadcast';

    // 2. Change the Label
    const submitLabel = isBroadcast ? "Post Job Now" : "Save & Find Pros";

    // 3. Change the Icon
    const SubmitIcon = isBroadcast ? Megaphone : UserPlus;

    const handleFilesChange = (newFiles) => {
        onUpdate('images', newFiles);
    };

    return (
        <div className={styles.jobDetailNew_card}>
            <div className={styles.jobDetailNew_cardHeader}>
                <h3 className={styles.jobDetailNew_cardTitle}>
                    <Camera className={`${styles.jobDetailNew_iconMd} ${styles.jobDetailNew_textAmber600}`} />
                    Job Files (Optional)
                </h3>
            </div>
            <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY6}`}>
                
                <JobFileUploader 
                    currentFiles={jobData.images}
                    onFilesChange={handleFilesChange}
                />

                <div style={{display: 'flex', justifyContent: 'space-between', marginTop: '2rem'}}>
                    <button onClick={onBack} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonOutline}`}>
                        <ArrowLeft size={16} style={{marginRight: '8px'}}/> Back
                    </button>
                    
                    <button 
                        onClick={onSubmit} 
                        className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonPrimary}`}
                        style={!isBroadcast ? { backgroundColor: '#10B981', borderColor: '#10B981', color: 'white' } : {}}
                    >
                        <SubmitIcon size={16} style={{marginRight: '8px'}}/> 
                        {submitLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};


export default JobCreationWizard;
